import { z } from 'zod';
import type {
    GetOrganizationLegalOverviewResponse,
    LegalHistorySearchRequest,
    OrganizationAgreementSignatureHistoryItem,
    OrganizationUserAgreementInEffect,
    OrganizationUserAgreementSource,
    SearchOrganizationAgreementSignaturesPageResponse,
} from '../legal-organization-overview.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { SystemDisplayTimeZoneSchema } from '../timezone.utils';
import { NonEmptyStringSchema } from './common.schemas';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import {
    LegalDocumentAssignmentPublicSchema,
    LegalDocumentsOpaqueCursorQuerySchema,
} from './legal-documents.schemas';

export const LegalHistorySearchRequestSchema: z.ZodType<LegalHistorySearchRequest> = z
    .object({
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: LegalDocumentsOpaqueCursorQuerySchema,
    })
    .strict();

export const OrganizationUserAgreementSourceSchema: z.ZodType<OrganizationUserAgreementSource> =
    z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('own') }).strict(),
        z
            .object({
                kind: z.literal('inherited'),
                organization_uuid: z.string().uuid(),
                organization_name: NonEmptyStringSchema,
            })
            .strict(),
        z.object({ kind: z.literal('platform_default') }).strict(),
    ]);

export const OrganizationUserAgreementInEffectSchema: z.ZodType<OrganizationUserAgreementInEffect> =
    z
        .object({
            legal_document_assignment: LegalDocumentAssignmentPublicSchema,
            source: OrganizationUserAgreementSourceSchema,
            required_user_count: z.number().int().nonnegative(),
            accepted_user_count: z.number().int().nonnegative(),
        })
        .strict();

export const GetOrganizationLegalOverviewResponseSchema: z.ZodType<GetOrganizationLegalOverviewResponse> =
    z
        .object({
            organization_uuid: z.string().uuid(),
            user_agreements: z.array(OrganizationUserAgreementInEffectSchema),
            awaiting_signature_count: z.number().int().nonnegative(),
            display_timezone: SystemDisplayTimeZoneSchema,
        })
        .strict();

export const OrganizationAgreementSignatureHistoryItemSchema: z.ZodType<OrganizationAgreementSignatureHistoryItem> =
    z
        .object({
            organization_legal_document_acceptance_uuid: z.string().uuid(),
            legal_document_assignment_uuid: z.string().uuid(),
            legal_document_uuid: z.string().uuid(),
            legal_document_title: NonEmptyStringSchema,
            legal_document_version: NonEmptyStringSchema,
            signed_ts: z.string().datetime(),
            signer_user_uuid: z.string().uuid(),
            signer_display_name: NonEmptyStringSchema,
            signer_authority_confirmed: z.boolean(),
            expires_ts: z.string().datetime().nullable(),
            withdrawn_ts: z.string().datetime().nullable(),
        })
        .strict();

export const SearchOrganizationAgreementSignaturesPageResponseSchema: z.ZodType<SearchOrganizationAgreementSignaturesPageResponse> =
    z
        .object({
            signatures: z.array(OrganizationAgreementSignatureHistoryItemSchema),
            pagination: OpaqueCursorPaginationSchema,
            display_timezone: SystemDisplayTimeZoneSchema,
        })
        .strict();
